import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useLazyQuery } from '@grandvisionhq/graphql';
import { useCartStorage } from './use-cart-storage.js';
import { useCartSettings } from '../context/CartSettings/context.js';
import { getUnrecoverableValidationErrors } from '../utils/get-unrecoverable-validation-errors.js';
import { GET_CART } from '../operations/queries/get-cart.js';

const useGetCart = ()=>{
    const settings = useCartSettings();
    const [cart, { setCartId , setCartHash , reset  }] = useCartStorage();
    const { cartId , cartHash , setContext  } = settings;
    const cartIdRef = cartId || cart.cartId;
    // This is needed to prefill the cart with the customer logic in the back-office flow.
    if (cartId && cartHash && (cart.cartId !== cartId || cart.cartHash !== cartHash)) {
        setCartId(cartId);
        setCartHash(cartHash);
    }
    const [getCart, { called , loading , data: data1 , error , refetch  }] = useLazyQuery(GET_CART, {
        onCompleted: (data)=>{
            const validationErrors = data.getCart.validationErrors;
            if (validationErrors && getUnrecoverableValidationErrors(validationErrors).length) {
                // If there are unsolvable validation errors, reset the cart
                reset();
            }
        },
        onError: console.error,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        context: {
            forwardHeaders: {
                'cart-hash': cart.cartHash
            }
        }
    });
    const refetchCallback = React.useCallback(()=>{
        if (cartIdRef) {
            // Refetch is undefined if the initial run has not been executed
            refetch ? refetch({
                id: cartIdRef
            }) : getCart({
                variables: {
                    id: cartIdRef
                }
            });
        }
    }, [
        getCart,
        refetch,
        cartIdRef
    ]);
    React.useEffect(()=>{
        if (cartIdRef) {
            getCart({
                variables: {
                    id: cartIdRef
                }
            });
        }
    }, [
        getCart,
        cartIdRef
    ]);
    const fetchByCartId = React.useCallback((cartIdParam, cartHashParam)=>{
        // Set cartId
        setCartId(cartIdParam);
        // Set carthash in localstorage
        if (cartHashParam) {
            setCartHash(cartHashParam);
        }
        // update react context with latest cartId & carthash
        setContext && setContext(swcHelpers.objectSpread({
            cartId: cartIdParam
        }, cartHashParam && {
            cartHash: cartHashParam
        }));
        // fetch cart based on latest cartId & carthash without relying on localstorage or context
        if (cartIdParam) {
            refetch ? refetch({
                id: cartIdParam
            }) : getCart(swcHelpers.objectSpread({
                variables: {
                    id: cartIdParam
                }
            }, cartHashParam && {
                context: {
                    forwardHeaders: {
                        'cart-hash': cartHashParam
                    }
                }
            }));
        }
    }, [
        setCartId,
        setCartHash,
        setContext,
        refetch,
        getCart
    ]);
    return {
        called,
        loading,
        data: !!cart.cartId ? data1 : null,
        error,
        refetch: refetchCallback,
        fetchByCartId,
        reset
    };
};

export { useGetCart };
//# sourceMappingURL=use-get-cart.js.map
